import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function TermsOfUse() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <img 
              src="https://customer-assets.emergentagent.com/job_health-cost-finder/artifacts/kj0ydf6o_HCD-1.png"
              alt="Healthcare Dollar Logo"
              className="h-12 w-auto cursor-pointer"
              onClick={() => navigate('/')}
            />
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="border-0 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8" />
              <CardTitle className="text-3xl">Terms of Use</CardTitle>
            </div>
            <p className="text-blue-100">Last Updated: January 2025</p>
          </CardHeader>
          
          <CardContent className="p-8 space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">1. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                By accessing and using Healthcare Dollar ("the Service"), you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use our Service. We reserve the right to modify these terms at any time, and your continued use constitutes acceptance of any changes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">2. Description of Service</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Healthcare Dollar provides cost estimation tools for elective medical procedures based on insurance plan information. Our Service includes:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Personalized out-of-pocket cost estimates</li>
                <li>Provider cost comparisons</li>
                <li>Insurance plan analysis and breakdown</li>
                <li>PDF export and email delivery of estimates (paid features)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">3. Important Disclaimers</h2>
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded mb-4">
                <p className="font-semibold text-gray-800 mb-2">ESTIMATES ARE NOT GUARANTEES</p>
                <p className="text-gray-700 leading-relaxed">
                  All cost estimates provided by Healthcare Dollar are approximations based on publicly available data, insurance plan information you provide, and market rates. Actual costs may vary significantly based on:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4 mt-2">
                  <li>Your specific insurance plan details and coverage</li>
                  <li>Provider billing practices and negotiated rates</li>
                  <li>Complications or additional procedures</li>
                  <li>Geographic location and facility type</li>
                  <li>Time of service and market changes</li>
                </ul>
              </div>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                <p className="font-semibold text-gray-800 mb-2">NOT MEDICAL OR FINANCIAL ADVICE</p>
                <p className="text-gray-700 leading-relaxed">
                  Healthcare Dollar does not provide medical advice, diagnosis, treatment recommendations, or professional financial advice. Always consult with your healthcare provider and insurance company for specific medical decisions and accurate cost information.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">4. User Responsibilities</h2>
              <p className="text-gray-700 mb-3">You agree to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Provide accurate insurance and personal information</li>
                <li>Maintain the confidentiality of your account credentials</li>
                <li>Use the Service only for lawful purposes</li>
                <li>Verify all cost estimates with your insurance provider and healthcare facility</li>
                <li>Not share, sell, or distribute content obtained from our Service</li>
                <li>Not attempt to circumvent payment requirements or security measures</li>
                <li>Not use automated systems (bots, scrapers) to access our Service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">5. Payment Terms</h2>
              <div className="space-y-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">5.1 Pricing</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Healthcare Dollar provides cost estimation services. Additional premium features may be available for healthcare organizations through B2B partnerships. Pricing varies based on organization subscription tier.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">5.2 Payment Processing</h3>
                  <p className="text-gray-700 leading-relaxed">
                    All payments are processed securely through Stripe. By making a purchase, you agree to Stripe's terms and conditions. We do not store your payment card information.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">5.3 B2B Partnerships</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Healthcare organizations can subscribe to Healthcare Dollar's B2B partnership program to provide cost transparency tools to their patients. Partnership subscriptions are billed monthly and can be cancelled at any time with notice.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">6. Intellectual Property</h2>
              <p className="text-gray-700 leading-relaxed">
                All content, features, and functionality of Healthcare Dollar, including but not limited to text, graphics, logos, software, and data compilations, are owned by Healthcare Dollar and protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">7. Limitation of Liability</h2>
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong>
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Healthcare Dollar is provided "AS IS" without warranties of any kind</li>
                  <li>We do not guarantee the accuracy, completeness, or reliability of cost estimates</li>
                  <li>We are not liable for any financial losses, medical decisions, or damages resulting from your use of our Service</li>
                  <li>Our total liability shall not exceed the amount you paid for the Service in the past 12 months</li>
                  <li>We are not responsible for third-party content, provider billing, or insurance coverage decisions</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">8. Indemnification</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify and hold Healthcare Dollar harmless from any claims, damages, losses, or expenses (including legal fees) arising from your use of the Service, violation of these Terms, or infringement of any third-party rights.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">9. Account Termination</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We reserve the right to suspend or terminate your account at any time for:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Violation of these Terms of Use</li>
                <li>Fraudulent or illegal activity</li>
                <li>Abuse of our Service or systems</li>
                <li>Non-payment of subscription fees</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">10. Data Accuracy</h2>
              <p className="text-gray-700 leading-relaxed">
                While we strive to provide accurate cost estimates based on publicly available data and the information you provide, we cannot guarantee the accuracy of all data. Healthcare costs are complex and subject to change. You should always verify costs directly with your insurance provider and healthcare facility before making decisions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">11. Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law provisions. Any disputes shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">12. Changes to Service</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time without notice. We may also change pricing with 30 days' notice to existing subscribers.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">13. Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-2">
                For questions about these Terms of Use, please contact us:
              </p>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-gray-800 font-semibold">Healthcare Dollar</p>
                <p className="text-gray-700">Email: <a href="mailto:info@healthcaredollar.tech" className="text-green-600 hover:underline">info@healthcaredollar.tech</a></p>
                <p className="text-gray-700">Website: healthcaredollar.tech</p>
              </div>
            </section>

            <div className="mt-8 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
              <p className="text-sm text-gray-700">
                <strong>By creating an account and using Healthcare Dollar, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.</strong>
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
